/**
 * Analytics Type Definitions
 *
 * Defines all types related to analytics data for the Admin Dashboard.
 * These types define the structure of analytics summary data from the backend.
 */

import type { AuditLogCategory, AuditLogSeverity } from './audit-log';

/**
 * Time Range Options for Analytics
 * Supported time ranges for analytics queries
 */
export type AnalyticsTimeRange = '1h' | '24h' | '7d' | '30d' | '90d';

/**
 * Time Range Display Labels
 * Human-readable labels for time ranges
 */
export const ANALYTICS_TIME_RANGE_LABELS: Record<AnalyticsTimeRange, string> = {
  '1h': 'Last Hour',
  '24h': 'Last 24 Hours',
  '7d': 'Last 7 Days',
  '30d': 'Last 30 Days',
  '90d': 'Last 90 Days',
};

/**
 * Time Series Data Point
 * Single data point in a time series chart
 */
export interface TimeSeriesDataPoint {
  /** ISO 8601 timestamp */
  timestamp: string;
  /** Event count at this timestamp */
  count: number;
}

/**
 * Top Event Type
 * Most frequent event types with counts
 */
export interface TopEventType {
  /** Event type name (e.g., "user.login") */
  eventType: string;
  /** Number of occurrences */
  count: number;
  /** Category of the event */
  category: AuditLogCategory;
}

/**
 * Endpoint Performance Data
 * Average response time metrics per endpoint
 */
export interface EndpointPerformance {
  /** API endpoint path */
  endpoint: string;
  /** Average response time in milliseconds */
  avgMs: number;
  /** Total number of requests */
  requestCount: number;
  /** Error rate percentage (0-100) */
  errorRate: number;
}

/**
 * Analytics Summary Interface
 * Complete analytics summary response from backend
 */
export interface AnalyticsSummary {
  /** Total number of events in the time range */
  totalEvents: number;
  /** Breakdown of events by category */
  eventsByCategory: Partial<Record<AuditLogCategory, number>>;
  /** Breakdown of events by severity */
  eventsBySeverity: Partial<Record<AuditLogSeverity, number>>;
  /** Top user by activity (null if no users) */
  topUser: {
    /** User ID of the most active user */
    userId: string;
    /** Number of events for this user */
    count: number;
  } | null;
  /** Time series data for event trends */
  timeSeries: TimeSeriesDataPoint[];
  /** Top event types by frequency */
  topEventTypes: TopEventType[];
  /** Average response times by endpoint */
  avgResponseTimes: EndpointPerformance[];
}
